import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Sparkles } from "lucide-react";

export default function LegalLayout({ title, lastUpdated, children, testid }) {
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div
      className="relative min-h-screen bg-black text-white"
      data-testid={testid || "legal-page"}
    >
      <div
        aria-hidden
        className="absolute -top-32 left-1/2 -translate-x-1/2 h-[420px] w-[680px] rounded-full bg-blue-600/10 blur-[140px]"
      />
      <div
        aria-hidden
        className="absolute top-40 -right-24 h-[360px] w-[360px] rounded-full bg-purple-600/10 blur-[140px]"
      />
      <div aria-hidden className="absolute inset-0 grid-bg opacity-60" />

      {/* Top bar */}
      <header className="relative">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 pt-8 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 group"
            data-testid="legal-logo-link"
          >
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-gradient-to-br from-blue-500/80 to-purple-600/80 ring-1 ring-white/10">
              <Sparkles className="h-3.5 w-3.5 text-white" />
            </span>
            <span className="text-sm font-semibold tracking-tight text-white">
              CV AI <span className="text-zinc-400 font-medium">PRO</span>
            </span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white transition-colors"
            data-testid="legal-back-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to site
          </Link>
        </div>
      </header>

      <main className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <p className="text-xs uppercase tracking-[0.18em] text-zinc-500">
            Legal
          </p>
          <h1
            className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white text-balance"
            data-testid="legal-headline"
          >
            {title}
          </h1>
          {lastUpdated && (
            <p className="mt-3 text-xs text-zinc-500">
              Last updated: {lastUpdated}
            </p>
          )}
        </motion.div>

        <motion.article
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="relative mt-12 rounded-3xl border border-white/10 bg-gradient-to-b from-zinc-950 to-black p-6 sm:p-10 space-y-7 text-zinc-300 leading-relaxed"
          data-testid="legal-article"
        >
          {children}
        </motion.article>

        <div className="mt-10 flex flex-col sm:flex-row gap-3 items-center justify-center text-xs text-zinc-500">
          <Link to="/privacy" className="hover:text-zinc-300 transition-colors">
            Privacy Policy
          </Link>
          <span className="hidden sm:inline text-zinc-700">·</span>
          <Link to="/terms" className="hover:text-zinc-300 transition-colors">
            Terms of Service
          </Link>
          <span className="hidden sm:inline text-zinc-700">·</span>
          <Link to="/refund" className="hover:text-zinc-300 transition-colors">
            Refund Policy
          </Link>
        </div>
      </main>
    </div>
  );
}

/* Reusable typography blocks */
export const Section = ({ heading, children }) => (
  <section className="space-y-3">
    <h2 className="text-lg sm:text-xl font-medium text-white tracking-tight">
      {heading}
    </h2>
    <div className="text-sm sm:text-[15px] text-zinc-400 space-y-3 leading-relaxed">
      {children}
    </div>
  </section>
);

export const Bullets = ({ items }) => (
  <ul className="space-y-2 list-disc list-outside pl-5 marker:text-zinc-600">
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

export const SupportLine = () => (
  <p className="text-sm text-zinc-400">
    Questions? Email us at{" "}
    <a
      href="mailto:getcvaipro@gmail.com"
      className="text-zinc-200 hover:text-white underline underline-offset-2"
    >
      getcvaipro@gmail.com
    </a>
    .
  </p>
);
